import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Sparkles, X, Send, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SUGGEST = ["How can I sell 50 units in 60 days?", "Suggest best audience for my projects", "Create a marketing plan for this quarter", "Generate a Meta campaign idea"];
const newSession = () => { const s = crypto.randomUUID(); localStorage.setItem("lx_copilot", s); return s; };

export default function Copilot() {
  const { reload } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(() => localStorage.getItem("lx_copilot") || newSession());
  const end = useRef(null);

  useEffect(() => {
    if (open) api.get("/copilot/history", { params: { session_id: session } }).then((r) => setMsgs(r.data)).catch(() => {});
  }, [open, session]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (msg) => {
    if (!msg.trim() || busy) return;
    setText("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    const append = (t) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + t }; return c; });
    try {
      const res = await fetch(`${API}/copilot/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("lx_token")}`, ...(localStorage.getItem("lx_org") ? { "X-Org-Id": localStorage.getItem("lx_org") } : {}) },
        body: JSON.stringify({ message: msg, session_id: session, page: loc.pathname }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); append(`**Error:** ${j.detail || res.statusText}`); return; }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const d = p.replace(/^data: /, "");
          if (d === "[DONE]") continue;
          try { const j = JSON.parse(d); if (j.delta) append(j.delta); if (j.error) append(`\n\n**Error:** ${j.error}`); } catch { /* partial */ }
        }
      }
    } finally {
      setBusy(false);
      reload();
    }
  };

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} data-testid="ai-copilot-trigger"
        className="fixed bottom-6 right-6 z-50 rounded-full h-14 w-14 grid place-items-center bg-emerald-500 text-slate-950 shadow-lg shadow-emerald-500/30 hover:scale-105 transition-transform">
        {open ? <X className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
      </button>
      {open && (
        <div className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[420px] h-[min(620px,75vh)] bg-white border border-slate-800 rounded-2xl shadow-2xl flex flex-col fade-up" data-testid="ai-copilot-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
            <div>
              <div className="font-bold flex items-center gap-2"><Sparkles className="h-4 w-4 text-cyan-400" />LaunchX360 Co-Pilot</div>
              <div className="text-[11px] text-slate-500 font-mono">context: {loc.pathname} · 1 credit / message</div>
            </div>
            <Button size="icon" variant="ghost" onClick={() => { setMsgs([]); setSession(newSession()); }} data-testid="ai-copilot-new-chat"><RotateCcw className="h-4 w-4" /></Button>
          </div>
          <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-3" data-testid="ai-copilot-messages">
            {!msgs.length && (
              <div className="space-y-2">
                <p className="text-sm text-slate-400">Ask anything about your projects, leads, campaigns or launch strategy.</p>
                {SUGGEST.map((s, i) => (
                  <button key={s} onClick={() => send(s)} data-testid={`ai-copilot-suggestion-${i}`} className="block w-full text-left text-sm px-3 py-2 rounded-lg border border-slate-800 hover:border-emerald-500/40 hover:bg-emerald-500/5 transition-colors">{s}</button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
                <div className={m.role === "user" ? "bg-emerald-500/15 border border-emerald-500/20 rounded-xl px-3 py-2 text-sm max-w-[85%]" : "prose-lx"}>
                  {m.role === "user" ? m.content : (m.content ? <ReactMarkdown>{m.content}</ReactMarkdown> : <span className="text-cyan-400 text-sm animate-pulse">Thinking...</span>)}
                </div>
              </div>
            ))}
            <div ref={end} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(text); }} className="p-3 border-t border-slate-800 flex gap-2">
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Ask Co-Pilot..." data-testid="ai-copilot-input" disabled={busy} />
            <Button type="submit" size="icon" disabled={busy} data-testid="ai-copilot-send"><Send className="h-4 w-4" /></Button>
          </form>
        </div>
      )}
    </>
  );
}
